import React, { useContext } from 'react';
import { Fragment } from 'react';
import CreateASpoolContext from '../../../../context/createASpool/createASpoolContext';
import themeContext from '../../../../context/theme/themeContext';
import useWidth from '../../../../hooks/useWidth';
import Button from '../../../../components/Button';
import CustomSlider from '../../../../components/CustomSlider';
import { TabsContext } from '../../../../components/Tabs';
import EstimatedALlocationDetails from './EstimatedAllocationDetails';
import ASSETS from '../../../../config/constants/assets.json';
import { AssetConstant } from '../../../../context/app/appTypes';
import { formatPercentages } from '../../../../helpers/formats';
import { mapRiskToRange } from '../../../../helpers/sdkHelper';
import appContext from '../../../../context/app/appContext';

const riskToleranceSetting = {
    min: 0,
    max: 10,
};

const RiskTolerance: React.FC = () => {
    const {
        selectedAsset,
        selectedStrategies,
        riskTolerance,
        baseApy,
        setRiskTolerance,
        setStepCompletedStatus,
    } = useContext(CreateASpoolContext);

    const { setDisabled } = useContext(TabsContext);
    const { theme } = useContext(themeContext);

    const { chainStrategies } = useContext(appContext);
    const windowWidth = useWidth();

    const handleChange = (value: number) => {
        setRiskTolerance(value);
    };

    const decrement = () => {
        if (riskTolerance <= riskToleranceSetting.min) return;

        setRiskTolerance(riskTolerance - 1);
    };

    const increment = () => {
        if (riskTolerance >= riskToleranceSetting.max) return;

        setRiskTolerance(riskTolerance + 1);
    };

    const setToMin = () => {
        setRiskTolerance(riskToleranceSetting.min);
    };

    const setToMax = () => {
        setRiskTolerance(riskToleranceSetting.max);
    };

    const handleBeforeChange = () => {
        setDisabled(true);
        setStepCompletedStatus(4, false);
    };

    const handleAfterChange = () => setDisabled(false);

    return (
        <div className={`risk-tolerance ${theme}`}>
            <span>
                Select the Risk Appetite of your Spool by using the slider or inputting a
                number between 0 and 10. <br /> 0 being Risk Averse, and 10 being Risk
                Seeking.
            </span>
            <div className='risk-tolerance-value'>
                <div className='slider-container'>
                    <div className='min'>
                        <Button
                            disabled={riskTolerance === riskToleranceSetting.min}
                            onClick={setToMin}
                        >
                            MIN
                        </Button>
                        <span className='risk-text'>Risk Averse</span>
                    </div>

                    <CustomSlider
                        range={riskToleranceSetting}
                        rangeLabel={{
                            max: 10,
                            min: 0,
                        }}
                        value={riskTolerance}
                        onChange={handleChange}
                        onBeforeChange={handleBeforeChange}
                        onAfterChange={handleAfterChange}
                        lineMultiple={windowWidth > 900 ? 2 : 1}
                    />
                    <div className='max'>
                        <Button
                            disabled={riskTolerance === riskToleranceSetting.max}
                            onClick={setToMax}
                        >
                            MAX
                        </Button>
                        <span className='risk-text'>Risk Seeking</span>
                    </div>
                </div>
                <div className='value-container'>
                    <div className='title'>Risk Appetite</div>
                    <div className='risk-value'>
                        <Button onClick={decrement}>-</Button>
                        <span>{mapRiskToRange(riskTolerance)}</span>
                        <Button onClick={increment}>+</Button>
                    </div>
                </div>
            </div>
            <div className='details'>
                <div className='asset-and-strategy'>
                    <div className='asset'>
                        <div className='title'>Asset:</div>
                        <div className='value'>
                            {selectedAsset
                                ? (ASSETS as AssetConstant)[selectedAsset]
                                : 'Asset (ERC20)'}
                        </div>
                    </div>
                    <div className='strategies'>
                        <div className='title'>Strategies</div>
                        <div className='value'>
                            {selectedStrategies.map((strategy, index) => (
                                <Fragment key={index}>
                                    {index !== 0 && ', '}
                                    {chainStrategies[strategy]?.name}
                                </Fragment>
                            ))}
                        </div>
                    </div>
                </div>
                <div className='projected-apy'>
                    <div className='title'>
                        Projected APY based on current selected Risk Appetite:
                    </div>
                    <div className='value'>{formatPercentages(baseApy || 0)}%</div>
                </div>
            </div>
            <EstimatedALlocationDetails />
        </div>
    );
};

export default RiskTolerance;
